/**
 * The AI Assistant.
 *
 * Note what the client may NOT send: a model name, a provider, or anything
 * resembling a price. It may ask for a *tier* — a commercial category CenoX
 * defines — and the server decides which model serves it and what that costs.
 * A client that could name a model could ask for the expensive one and be
 * billed at the cheap tier's rate, which is the same class of mistake as a
 * browser naming its own price at checkout.
 */

import { z } from 'zod';

export const AI_TIER_VALUES = ['default', 'escalation', 'premium'] as const;

export const aiChatRequestSchema = z.object({
  message: z.string().min(1, 'Ask a question').max(8_000),
  /**
   * Prior turns, oldest first.
   *
   * Sent by the client because the assistant holds no server-side session, and
   * capped because conversation history is re-sent on every turn — it is the
   * thing the customer actually pays for, and an unbounded history is an
   * unbounded bill.
   */
  history: z
    .array(
      z.object({
        role: z.enum(['user', 'assistant']),
        content: z.string().max(8_000),
      }),
    )
    .max(20)
    .optional(),
  tier: z.enum(AI_TIER_VALUES).optional(),
  /** Required for any tier above the default. */
  escalationReason: z.string().min(3).max(200).optional(),
  /**
   * Makes a retry safe: the same key returns the same answer and is charged
   * once. A timeout tells the caller nothing about whether the provider ran
   * the request, and it usually did.
   */
  idempotencyKey: z.string().min(8).max(100).optional(),
});

export type AiChatRequest = z.infer<typeof aiChatRequestSchema>;

/** What the assistant sends back. Costs are included so the UI can be honest. */
export interface AiChatResponse {
  text: string;
  /** The customer-facing name, never the supplier's model id. */
  label: string;
  tier: (typeof AI_TIER_VALUES)[number];
  tokensUsed: number;
  amountCharged: string;
  currencyCode: string | null;
  /** True when this answer was replayed from an identical earlier request. */
  replayed: boolean;
}

export interface AiTierView {
  tier: (typeof AI_TIER_VALUES)[number];
  label: string;
  /** Whether this business's plan includes it. */
  available: boolean;
}

/**
 * How AI is paid for, said plainly. No provider cost and no markup figure:
 * those are CenoX's, and a customer is told how pricing works, not its inputs.
 */
export interface AiPricingSummary {
  /** The basis of a CenoX-managed AI charge. */
  basis: string;
  topUpsAvailable: boolean;
  currencyCode: string;
  currentPlan: string;
  plans: Array<{
    plan: string;
    name: string;
    /** included = CenoX-managed AI with monthly credit; byok_only = bring your own key; custom = negotiated. */
    managedAi: 'included' | 'byok_only' | 'custom';
    includedMonthlyCredit: string | null;
  }>;
}

/** Whether Search the web is offered to this workspace, and its limits. */
export interface AiWebSearchAvailability {
  available: boolean;
  /** True when CenoX charges for a searched answer (never for an own-key workspace). */
  charged: boolean;
  maxCallsPerAnswer: number;
  dailyLimit: number;
  usedToday: number;
}

export interface AiTiersResponse {
  data: AiTierView[];
  pricing: AiPricingSummary;
  /** Absent from older servers; treat absence as not available. */
  webSearch?: AiWebSearchAvailability;
}

/** One source, exactly as the search provider cited it. */
export interface AiSource {
  url: string;
  title: string | null;
}

// ── Usage ───────────────────────────────────────────────────────────────────

/**
 * The four kinds of token, kept apart.
 *
 * They are priced differently — cached input is cheaper than fresh input, a
 * cache write costs a premium over it, and output is the expensive one — so a
 * single total hides the thing an owner can actually act on. A workspace whose
 * bill is mostly output needs shorter answers; one that is mostly fresh input
 * is re-sending context that caching could have covered.
 */
export interface AiUsageTokens {
  input: number;
  cached: number;
  cacheWrite: number;
  output: number;
  total: number;
}

/** Usage grouped by something — a tier, or a CenoX feature. */
export interface AiUsageGroup {
  key: string;
  label: string;
  requests: number;
  tokens: AiUsageTokens;
  charged: string;
}

/**
 * The ceilings in force, and where this workspace stands against them.
 *
 * Published so the limit is visible before it is hit. Null means no ceiling of
 * that kind, which is deliberately different from a ceiling of zero.
 */
export interface AiUsageLimits {
  maxChargePerRequest: string | null;
  dailySpendCap: string | null;
  monthlySpendCap: string | null;
  dailyTokenCap: number | null;
  spentToday: string;
  tokensToday: number;
}

export interface AiUsageEntry {
  at: string;
  feature: string;
  /** The tier's customer-facing name, e.g. "CenoX AI (Advanced)". */
  label: string;
  tokens: AiUsageTokens;
  /** To the kobo, for display. */
  charged: string;
  /**
   * What was actually charged, at the ledger's eight places. A row rounded to
   * the kobo can differ from what it moved the total by; this is the figure
   * that settles it.
   */
  chargedExact: string;
  status: string;
}

/**
 * What the Usage screen shows.
 *
 * Charges only. What CenoX pays its provider, the markup and the margin are
 * recorded on every usage event and never published here — the same rule the
 * chat response follows. A customer's bill is their business; CenoX's cost is
 * not.
 */
export interface AiUsageView {
  currencyCode: string;
  planName: string;
  billingMode: 'cenox_managed' | 'bring_your_own_key' | 'unavailable';
  includedCredit: string;
  includedUsed: string;
  includedRemaining: string;
  topUpBalance: string;
  chargedThisCycle: string;
  cycleStart: string;
  cycleEnd: string;
  /** Whether the window above is a month, or the whole invoice period. */
  monthlyAllowance: boolean;
  requestsThisCycle: number;
  tokensThisCycle: AiUsageTokens;
  /** Web searches the provider reported this cycle. Absent from older servers. */
  webSearchesThisCycle?: number;
  byTier: AiUsageGroup[];
  byFeature: AiUsageGroup[];
  limits: AiUsageLimits;
  history: AiUsageEntry[];
}

// ── Conversations ───────────────────────────────────────────────────────────

/**
 * Who may read a thread.
 *
 * `private` is the default and means the author alone — not their manager, not
 * the owner. Sharing is a deliberate act by the person whose words they are.
 */
export const AI_CONVERSATION_VISIBILITY = ['private', 'project', 'workspace'] as const;
export type AiConversationVisibility = (typeof AI_CONVERSATION_VISIBILITY)[number];

export const aiConversationStartSchema = z.object({
  title: z.string().trim().max(200).nullish(),
  tier: z.enum(AI_TIER_VALUES).nullish(),
  /** Start this thread inside a project. Null or absent means the flat list. */
  projectId: z.string().uuid().nullish(),
});
export type AiConversationStartInput = z.infer<typeof aiConversationStartSchema>;

export const aiConversationUpdateSchema = z.object({
  title: z.string().trim().max(200).nullish(),
  visibility: z.enum(AI_CONVERSATION_VISIBILITY).optional(),
  /*
    Move the thread. `null` means out of every project, back to the flat list,
    which is why this is nullable rather than optional-only: "absent" has to
    mean "leave it where it is" and `null` has to mean "take it out", and one
    field cannot carry both unless the difference is spelled.
  */
  projectId: z.string().uuid().nullish(),
  /**
   * Yes, I know the existing messages go too.
   *
   * Required to widen the visibility of a conversation that already has
   * messages in it. Sharing a thread shares its history, and an answer given
   * weeks ago may hold a figure the new reader could not ask for today — no
   * permission check can take that back once it has been read.
   *
   * Enforced on the server rather than left to the screen, because the screen
   * is not the only caller and a warning nobody has to answer is decoration.
   */
  acknowledgeHistory: z.boolean().optional(),
});
export type AiConversationUpdateInput = z.infer<typeof aiConversationUpdateSchema>;

export const aiAskSchema = z.object({
  message: z.string().trim().min(1).max(8_000),
  tier: z.enum(AI_TIER_VALUES).optional(),
  escalationReason: z.string().trim().max(400).optional(),
  idempotencyKey: z.string().trim().min(8).max(200).optional(),
  /**
   * Search the web for this answer. A request: the server decides whether it
   * is offered, how many searches it may run, and refuses past the day's
   * limit. A searched answer is given no business records.
   */
  webSearch: z.boolean().optional(),
});
export type AiAskInput = z.infer<typeof aiAskSchema>;

export interface AiConversationView {
  id: string;
  title: string | null;
  /** The project it sits in, or null for the flat list. */
  projectId: string | null;
  visibility: AiConversationVisibility;
  tier: string | null;
  /** True when the caller started it. Only then may they rename or delete it. */
  mine: boolean;
  /** Named only for somebody else's shared thread. */
  authorName: string | null;
  messageCount: number;
  lastMessageAt: string | null;
  createdAt: string;
}

export interface AiMessageView {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  /** `web`: live search, no business records. `business`: the business snapshot. */
  contextKind?: 'business' | 'web' | 'general' | 'project' | null;
  /** Searches the provider reported. Null: search was not enabled for this answer. */
  webSearchCalls?: number | null;
  /** The provider's citations. Null: search was not enabled. */
  sources?: AiSource[] | null;
  createdAt: string;
  /**
   * The project documents this answer cited, in the order of their numbers.
   * Empty for a question, and for an answer that cited nothing.
   */
  citations: AiCitation[];
  /**
   * An answer drawn from project documents this reader may not read now —
   * not shared with them, or a document it used has since been deleted.
   * `content` is then a fixed notice, not the answer.
   */
  withheld: boolean;
}

/**
 * One `[n]` in an answer, and where it points.
 *
 * Resolved when it is READ, under the reader's own access, not when it was
 * written. A document deleted since, or a project the reader cannot see —
 * never could, or has been removed from — comes back `available: false` with
 * no title, page or link: the answer still says "[2]", and [2] says it is not
 * available to them.
 */
export interface AiCitation {
  n: number;
  available: boolean;
  projectId: string | null;
  fileId: string | null;
  title: string | null;
  /** 'p.3', "'Rates 2026'!A3:C5", 'lines 1-40'. */
  locator: string | null;
  /** The passage's position in the file's extracted text, for `/text?offset=`. */
  ordinal: number | null;
}

/** What the documents contributed to one answer, sent with `done`. */
export interface AiRetrievalSummary {
  /** Readable documents searched for this question. */
  searched: number;
  /** Documents in the project that could not be searched, and are named to the model as such. */
  notSearched: number;
  /** Passages handed to the model. */
  passages: number;
  /** Estimated tokens of document text in the prompt. Attribution; the charge is the provider's count. */
  tokens: number;
  /** The per-question ceiling those tokens were held under. */
  budget: number;
  /** More passages matched than fit under the ceiling. */
  truncated: boolean;
}

/**
 * The events a streamed answer sends, as `event:` names over SSE.
 *
 *   `chunk` — a fragment of the answer. Many.
 *   `done`  — the answer finished; carries what it cost and the saved ids.
 *   `error` — it did not finish; carries a message fit to show somebody.
 *
 * `done` and `error` are mutually exclusive and exactly one always arrives, so
 * a client never has to guess from a closed socket whether it was charged.
 */
export const AI_STREAM_EVENTS = ['chunk', 'done', 'error'] as const;

export interface AiStreamDone {
  conversationId: string;
  messageId: string;
  label: string;
  tier: string;
  tokensUsed: number;
  amountCharged: string;
  currencyCode: string;
  replayed: boolean;
  /** Present only when search was enabled for this answer. */
  webSearch?: { searched: boolean; calls: number; sources: AiSource[] };
  /** Citations the answer made that point at passages it was really given. */
  citations: AiCitation[];
  /**
   * `[n]` markers in the answer that match no passage it was given — a
   * citation the model invented. Shown as unverified, never linked.
   */
  unverifiedCitations: number[];
  /** Null when the thread is in no project, or the project holds no files. */
  documents: AiRetrievalSummary | null;
}

/**
 * A project: a folder for conversations, with standing instructions.
 *
 * Private by default. Sharing exists in the schema and is not yet a route —
 * the owner is the only person who can see one until that lands.
 */
export const AI_PROJECT_VISIBILITY = ['private', 'workspace'] as const;
export type AiProjectVisibility = (typeof AI_PROJECT_VISIBILITY)[number];

/**
 * The instruction cap, in characters.
 *
 * Instructions are re-sent and re-billed on every turn in the project, like
 * the system prompt — so this is a cost ceiling, not a storage one. Roughly
 * 500 tokens, about half the current system prompt. An owner pasting a page of
 * policy into a project is buying it on every question they ask.
 */
export const AI_PROJECT_INSTRUCTIONS_MAX = 2_000;

export const aiProjectCreateSchema = z.object({
  name: z.string().trim().min(1).max(120),
  instructions: z.string().trim().max(AI_PROJECT_INSTRUCTIONS_MAX).nullish(),
});
export type AiProjectCreateInput = z.infer<typeof aiProjectCreateSchema>;

export const aiProjectUpdateSchema = z.object({
  name: z.string().trim().min(1).max(120).optional(),
  instructions: z.string().trim().max(AI_PROJECT_INSTRUCTIONS_MAX).nullish(),
  /** True to archive, false to restore. Archiving hides, it never deletes. */
  archived: z.boolean().optional(),
});
export type AiProjectUpdateInput = z.infer<typeof aiProjectUpdateSchema>;

export interface AiProjectView {
  id: string;
  name: string;
  instructions: string | null;
  visibility: AiProjectVisibility;
  archivedAt: string | null;
  /** Threads currently in it. Counts only what the reader may see. */
  conversationCount: number;
  /** Whether the reader owns it. Only an owner may change it. */
  mine: boolean;
  /** Named only for somebody else's shared project. */
  ownerName: string | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * What somebody a project was shared with may do in it.
 *
 * The owner is not in this list: a project's owner is `ai_projects.user_id`,
 * never a membership row, so ownership cannot be granted away by writing one
 * — and cannot be lost by deleting one.
 */
export const AI_PROJECT_MEMBER_ROLES = ['editor', 'viewer'] as const;
export type AiProjectMemberRole = (typeof AI_PROJECT_MEMBER_ROLES)[number];

/** Owner included, for anything that displays the three together. */
export const AI_PROJECT_ROLES = ['owner', 'editor', 'viewer'] as const;
export type AiProjectRole = (typeof AI_PROJECT_ROLES)[number];

export const aiProjectInviteSchema = z.object({
  /** An existing member of this business, by user id. Never an email: this
   *  invites somebody who is already authorised here, and does not create a
   *  way into the workspace. */
  userId: z.string().uuid(),
  role: z.enum(AI_PROJECT_MEMBER_ROLES).default('viewer'),
});
export type AiProjectInviteInput = z.infer<typeof aiProjectInviteSchema>;

export const aiProjectMemberUpdateSchema = z.object({
  role: z.enum(AI_PROJECT_MEMBER_ROLES),
});
export type AiProjectMemberUpdateInput = z.infer<typeof aiProjectMemberUpdateSchema>;

export interface AiProjectMemberView {
  userId: string;
  fullName: string | null;
  email: string | null;
  role: AiProjectRole;
  /** Null for the owner, who was never granted anything. */
  grantedAt: string | null;
}

/**
 * What one project has cost.
 *
 * Attributed through the conversations in it, which is why it is a report
 * rather than a column: a charge belongs to the usage event that incurred it,
 * and moving a thread between projects must not rewrite history. A thread that
 * has left a project takes its spend with it, and that is correct — the
 * question "what is this project costing" is about what is in it now.
 */
export interface AiProjectUsageView {
  projectId: string;
  projectName: string;
  requests: number;
  tokens: number;
  /** Decimal string, in the business's currency. */
  charged: string;
  currencyCode: string;
  conversations: number;
  /**
   * Answers in this project that no charge can be attributed to.
   *
   * Every message written before per-project attribution existed carries no
   * usage event, because the column was never populated. Without this number a
   * project full of older conversations reports zero spend, which is
   * indistinguishable from a project nobody has used — and reporting "nothing"
   * for "we cannot say" is the one thing this product does not do.
   *
   * It falls to zero on its own as old threads stop being the ones people use.
   * No backfill: a charge belongs to the request that incurred it, and there
   * is no honest way to decide afterwards which answer a historical charge
   * paid for.
   */
  unattributedAnswers: number;
  /**
   * Estimated tokens of document text sent with questions in this project.
   *
   * Part of `tokens`, not added to it: shown on its own because it is the
   * cost an owner controls, by deciding what the project holds.
   */
  retrievalTokens: number;
  /** The per-question ceiling on document text, shown next to the file list. */
  retrievalBudget: number;
}

/** How far a project document has got, and what may be done with it. */
export const AI_FILE_STATUSES = [
  'pending_scan',
  'infected',
  'pending',
  'extracted',
  'needs_ocr',
  'unsupported',
  'failed',
] as const;
export type AiFileStatus = (typeof AI_FILE_STATUSES)[number];

export interface AiProjectFileView {
  id: string;
  title: string;
  mimeType: string | null;
  byteSize: number;
  status: AiFileStatus;
  /** Why, when the status alone does not say. */
  statusDetail: string | null;
  pageCount: number | null;
  /** Pieces of extracted text. Zero until the document has been read. */
  chunkCount: number;
  /**
   * The text-search configuration its words were indexed with: `simple`
   * (exact words, any script) or a language Postgres can stem. Null until read.
   */
  language: string | null;
  /** When a malware scanner gave its verdict. Null while in quarantine. */
  scannedAt: string | null;
  /**
   * Computed on the server, not re-derived by the screen.
   *
   * A quarantined or infected file is neither, and so is anything whose status
   * nobody planned for — a status we do not recognise is not a pass.
   */
  downloadable: boolean;
  searchable: boolean;
  mine: boolean;
  /** Named only when somebody else uploaded it. */
  uploaderName: string | null;
  createdAt: string;
  processedAt: string | null;
  /**
   * How a scan's text was got, or why it has not been. Null for a file that
   * never needed OCR.
   */
  ocr: AiFileOcrView | null;
}

/** OCR's state for one file (phase 5). */
export interface AiFileOcrView {
  /**
   * no_engine, awaiting_consent, project_not_allowed, over_monthly_limit,
   * done, failed — or null while it has not been considered yet.
   */
  state: string | null;
  engine: string | null;
  /** True when its page images were sent to an external provider. */
  external: boolean;
  pages: number | null;
  /** Mean recognition confidence 0–1, when the engine reports one. */
  confidence: number | null;
}

/** The business's OCR settings, as an owner sees them. */
export interface AiOcrSettingsView {
  /** What consenting means, in words, and which version of the words. */
  disclosure: string;
  disclosureVersion: string;
  externalConsent: { grantedAt: string; grantedByName: string | null; version: string } | null;
  /** A consent given to an older wording, which no longer counts. */
  staleConsentVersion: string | null;
  /** The engines this deployment has, or null for none. */
  localEngine: string | null;
  externalEngine: string | null;
  /** External pages per month, set by CenoX. 0: none included. */
  pagesPerMonth: number;
  pagesUsedThisMonth: number;
  maxPagesPerFile: number;
  /** What one page would cost, in the business's currency, before it is read. */
  perPageEstimate: { amount: string; currency: string } | null;
}

export const aiOcrConsentSchema = z.object({
  granted: z.boolean(),
  /** The disclosure version the person was shown. Required to grant. */
  version: z.string().max(64).optional(),
});

export const aiProjectOcrSchema = z.object({ externalAllowed: z.boolean() });

/**
 * The text read out of a document, as the index holds it.
 *
 * Shown so a person can check what the assistant will be quoting BEFORE it
 * quotes it — a spreadsheet whose merged cells came out wrong is found here,
 * not in an answer to a customer.
 */
export interface AiProjectFileText {
  fileId: string;
  status: AiFileStatus;
  statusDetail: string | null;
  language: string | null;
  totalChunks: number;
  chunks: Array<{ ordinal: number; locator: string | null; content: string; tokenEstimate: number }>;
}
